import { useMemo } from 'react'
import type { ApolloError } from '@apollo/client'
import {
  buildSingleShellPlaylistDetails,
  collectShellContentItems,
  type PlaylistDetailsEntry,
} from './content-utils'
import type { PlaylistShell } from '../../types'
import useTitleGroupHydration, {
  type UseTitleGroupHydrationOptions,
} from './useTitleGroupHydration'

/**
 * Per-row hydration composition.
 *
 * Wraps `useTitleGroupHydration` with the shell-builder pass, returning
 * the ready-to-render `PlaylistDetailsEntry` for ONE playlist. The
 * critical property: each row variant calls this hook in its own
 * `useQuery` scope, so a sibling row's hydration completing no longer
 * ticks this row's `React.memo` shallow-equal pass. That kills the
 * page-wide cascade the legacy shared-map pattern triggered on every
 * row resolve / `cache.modify`.
 *
 * Non-shell hydration paths (Watch History, In-Progress,
 * `CommunityProfilePage.tsx`'s watch-history block) use
 * `useTitleGroupHydration` directly — they don't have a shell to
 * build through.
 */

const EMPTY_DETAILS: PlaylistDetailsEntry = Object.freeze({
  items: [],
  primaryTopic: undefined,
  totalDuration: null,
  expectedItemCount: 0,
}) as PlaylistDetailsEntry

interface UseRowHydrationResult {
  details: PlaylistDetailsEntry
  isHydrating: boolean
  error: ApolloError | undefined
}

export default function useRowHydration (
  shellPlaylist: PlaylistShell | null | undefined,
  language: string,
  options?: UseTitleGroupHydrationOptions,
): UseRowHydrationResult {
  // titleGroupIds derived from the shell. `collectShellContentItems`
  // already handles the `${contentId}-${contentType}` fallback for
  // items whose `titleGroupId` is null/empty and dedupes within the
  // playlist.
  const titleGroupIds = useMemo<readonly string[]>(() => {
    if (!shellPlaylist) return []
    return collectShellContentItems([shellPlaylist]).map((it) => it.titleGroupId)
  }, [shellPlaylist?.playlistItems])

  // Defer to the primitive. `skip` honoured if caller passed one; on
  // top of that, the primitive itself short-circuits when
  // `titleGroupIds.length === 0`.
  const { contentMap, loading, error } = useTitleGroupHydration(
    titleGroupIds,
    language,
    options,
  )

  const details = useMemo<PlaylistDetailsEntry>(() => {
    if (!shellPlaylist) return EMPTY_DETAILS
    return buildSingleShellPlaylistDetails(shellPlaylist, contentMap)
  }, [shellPlaylist, contentMap])

  return {
    details,
    isHydrating: loading,
    error,
  }
}

export type { UseRowHydrationResult }
